// commitmentDisplay.js — pure display helpers for commitments (Up next card +
// commitments calendar). Kept framework-free so they are unit-tested directly; the
// JSX in Scorify.jsx maps STATUS_META tones to theme colors.

// A commitment's status, as shape + tone (NOT color alone — open and missed are both
// reddish in the theme, so open is a hollow ring and missed is a solid fill). The JSX
// resolves `tone` to a theme token and `hollow` to ring-vs-fill.
export const STATUS_META = {
  open: { label: 'Open', tone: 'accent', hollow: true },
  needs_confirmation: { label: 'Needs confirmation', tone: 'warning', hollow: false },
  done: { label: 'Done', tone: 'success', hollow: false },
  missed: { label: 'Missed', tone: 'danger', hollow: false },
  dropped: { label: 'Removed', tone: 'dim', hollow: false },
};
export const statusMeta = (status) => STATUS_META[status] || STATUS_META.open;

// 24h 'HH:MM' -> '5:20 PM', or null.
export function fmt12h(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm || '');
  if (!m) return null;
  let h = +m[1]; if (h > 23) return null;
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
}

// A commitment's due_date ('YYYY-MM-DD', a LOCAL calendar date) for display,
// relative to the user's local today: "Today" / "Tomorrow" / "Yesterday", a
// weekday name for 2-6 days ahead ("Thursday"), else "Thu, Oct 22". Never the
// raw ISO string. Parsed as local calendar parts — new Date('YYYY-MM-DD') would
// be UTC midnight, the previous day west of UTC. Day math in UTC so a DST
// change can't round a day away.
const _parts = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? [+m[1], +m[2] - 1, +m[3]] : null; };
export function fmtDueDay(dueDate, todayStr) {
  const d = _parts(dueDate), t = _parts(todayStr);
  if (!d) return null;
  if (t) {
    const diff = Math.round((Date.UTC(...d) - Date.UTC(...t)) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === -1) return 'Yesterday';
    if (diff >= 2 && diff <= 6) return new Date(...d).toLocaleDateString('en-US', { weekday: 'long' });
  }
  return new Date(...d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// The display title for a commitment: the stored activity (metric_ref) title-cased,
// else the commitment text with trailing scheduling words trimmed.
export function commitLabel(c) {
  const r = ((c && c.metric_ref) || '').trim();
  if (r) return r.replace(/\b\w/g, (x) => x.toUpperCase());
  let t = String((c && c.text) || '').trim();
  t = t.replace(/\b(tomorrow|today|tonight|this (weekend|week)|next week)\b.*$/i, '')
    .replace(/\bat\s+\d{1,2}(:\d{2})?\s*(a\.?m\.?|p\.?m\.?)?\b.*$/i, '').trim();
  return t || 'Your plan';
}

// Does the raw text carry detail beyond the title + scheduling/time words? Used to
// avoid repeating "Run" / "Run on Friday" in the day detail: strip the label, weekday,
// relative-time and clock words, and if nothing meaningful is left, the text is just the
// title and should not be shown again.
export function textAddsDetail(c) {
  const text = String((c && c.text) || '');
  if (!text.trim()) return false;
  const label = commitLabel(c);
  let rest = text;
  if (label) rest = rest.replace(new RegExp(label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig'), ' ');
  rest = rest
    .replace(/\b(on\s+)?(mon|tues|wednes|thurs|fri|satur|sun)day\b/ig, ' ')
    .replace(/\b(tomorrow|today|tonight|this|that|the|a|an|next|last|coming|weekend|week|morning|afternoon|evening|night|at|by|for|session|as|workout)\b/ig, ' ')
    .replace(/\b\d{1,2}(:\d{2})?\s*(a\.?m\.?|p\.?m\.?)?\b/ig, ' ')
    .replace(/[^a-z0-9]+/ig, ' ')
    .trim();
  return rest.length > 1;
}
